import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { supabase } from "@/integrations/supabase/client";

/**
 * Le notifiche push, lato telefono.
 *
 * COSA FA, IN BREVE
 * Chiede il permesso, si fa dare dal sistema operativo l'indirizzo di consegna
 * di QUESTO telefono (il "token"), e lo scrive in `dispositivi_push`. Da li' in
 * poi il telefono e' raggiungibile: chi manda davvero e' la edge function
 * `invia-notifica`.
 *
 * PERCHE' IL TOKEN SI RISCRIVE A OGNI AVVIO
 * Non e' un identificativo stabile: Apple e Google lo cambiano quando vogliono
 * (aggiornamento di sistema, ripristino da backup, reinstallazione). Un token
 * vecchio non da' errore quando si manda: la notifica risulta partita e non
 * arriva a nessuno. L'unico modo di accorgersene e' non fidarsi mai di quello
 * salvato ieri.
 *
 * SUL SITO NON SUCCEDE NIENTE, ED E' GIUSTO COSI'
 * Le notifiche del browser sono un'altra cosa (Web Push, con un service worker
 * e un altro tipo di chiave). Qui la funzione esce subito: meglio niente che
 * una richiesta di permesso che poi non consegna nulla.
 *
 * IL PERMESSO SI CHIEDE UNA VOLTA SOLA, E NON AL PRIMO AVVIO
 * Se l'utente dice no, il sistema non ripropone piu' la finestra: la si
 * riattiva solo dalle impostazioni del telefono. Per questo la chiamata sta
 * DOPO il login e non sulla schermata di apertura - a quel punto si sa gia'
 * cos'e' l'app, e la richiesta ha un senso leggibile.
 */

/** Vero solo dentro l'app installata: sul sito le push non passano di qui. */
export function siPossonoRicevereNotifiche(): boolean {
  return Capacitor.isNativePlatform();
}

let giaAvviato = false;
/** Il token di QUESTO telefono, com'e' arrivato dalla registrazione. */
let tokenDiQuestoTelefono: string | null = null;

/**
 * Registra questo telefono per le notifiche, se si puo'.
 *
 * Non lancia mai eccezioni: un permesso negato o una registrazione fallita non
 * devono impedire di usare l'app. Restituisce cosa e' successo, per chi vuole
 * mostrarlo (le impostazioni) e per i log.
 */
export async function registraNotifiche(): Promise<
  "registrato" | "negato" | "non-supportato" | "errore"
> {
  if (!siPossonoRicevereNotifiche()) return "non-supportato";
  // Le listener si agganciano una volta per sessione: rifarlo a ogni cambio di
  // rotta significherebbe salvare lo stesso token dieci volte.
  if (giaAvviato) return "registrato";
  giaAvviato = true;

  try {
    let permesso = await PushNotifications.checkPermissions();
    if (permesso.receive === "prompt" || permesso.receive === "prompt-with-rationale") {
      permesso = await PushNotifications.requestPermissions();
    }
    if (permesso.receive !== "granted") {
      giaAvviato = false;
      return "negato";
    }

    await PushNotifications.addListener("registration", async (t) => {
      await salvaToken(t.value);
    });

    await PushNotifications.addListener("registrationError", (e) => {
      // Su Android questo e' quasi sempre il google-services.json mancante o
      // di un altro progetto Firebase; su iOS, la capability Push non accesa
      // sull'identificativo dell'app.
      console.error("push_registrazione_ko", e);
    });

    await PushNotifications.register();
    return "registrato";
  } catch (e) {
    giaAvviato = false;
    console.error("push_avvio_ko", e);
    return "errore";
  }
}

/**
 * Scrive il token in `dispositivi_push`.
 *
 * `onConflict: "token"` e non l'inserimento semplice: lo stesso telefono puo'
 * passare di mano - il tecnico esce e entra un collega - e senza questo
 * resterebbero due righe, con il primo che continua a ricevere le notifiche
 * destinate al secondo. Con il conflitto sul token, la riga cambia proprietario.
 */
async function salvaToken(token: string): Promise<void> {
  const { data } = await supabase.auth.getUser();
  const utente = data?.user;
  if (!utente || !token) return;
  tokenDiQuestoTelefono = token;

  const { error } = await supabase.from("dispositivi_push").upsert(
    {
      user_id: utente.id,
      token,
      piattaforma: Capacitor.getPlatform() === "ios" ? "ios" : "android",
      visto_il: new Date().toISOString(),
    },
    { onConflict: "token" },
  );
  if (error) console.error("push_token_non_salvato", error.message);
}

/**
 * Toglie questo telefono dalla rubrica.
 *
 * Va chiamata all'uscita dall'account: senza, il telefono resterebbe legato a
 * chi si e' disconnesso e le notifiche di Datacom continuerebbero ad arrivare
 * sul dispositivo di un collega.
 */
export async function dimenticaQuestoTelefono(): Promise<void> {
  if (!siPossonoRicevereNotifiche()) return;
  try {
    const { data } = await supabase.auth.getUser();
    if (!data?.user) return;
    // SOLO questo telefono. Fino al 05/09/2026 (audit, Sol 10) si cancellavano
    // tutte le righe dell'utente: uscire dall'app sul telefono spegneva le
    // notifiche anche sul tablet in ufficio. Se il token di questa sessione
    // non e' noto (l'app e' ripartita senza registrarsi), la riga resta: al
    // prossimo accesso su questo telefono l'upsert sul token la fa cambiare
    // proprietario (vedi salvaToken).
    if (tokenDiQuestoTelefono) {
      await supabase
        .from("dispositivi_push")
        .delete()
        .eq("user_id", data.user.id)
        .eq("token", tokenDiQuestoTelefono);
      tokenDiQuestoTelefono = null;
    }
    await PushNotifications.removeAllListeners();
    giaAvviato = false;
  } catch (e) {
    console.error("push_uscita_ko", e);
  }
}
